/**
 * Utility to round financial numbers to 2 decimal places to prevent floating-point drift.
 */
const round = (num) => {
  return Math.round((parseFloat(num) || 0) * 100) / 100;
};

export const getFieldHoursForExpense = (fieldHours, expenseId) => {
  if (!fieldHours) return [];
  return fieldHours.filter((fh) => fh.expense_id === expenseId);
};

export const calculateTotals = (expenses = [], fieldHours = [], adminFeePercent = 0) => {
  const boxes = expenses.filter((exp) => exp.expense_type === "box");
  const fields = expenses.filter((exp) => exp.expense_type === "field");

  let totalUsedQuantity = 0;
  let totalTotalQuantity = 0;
  let unallocatedExpenses = [];

  const totalBoxCost = boxes.reduce((sum, exp) => {
    const tQty = parseFloat(exp.total_quantity) || 1;
    const uQty = parseFloat(exp.used_quantity || 0);
    totalUsedQuantity += uQty;
    totalTotalQuantity += tQty;

    const pricePerUnit = round(parseFloat(exp.total_amount) / tQty);
    const cost = round(pricePerUnit * uQty);

    if (cost === 0 && parseFloat(exp.total_amount) > 0) {
      unallocatedExpenses.push(exp.description || exp.item_name);
    }

    return round(sum + cost);
  }, 0);

  let totalHours = 0;
  const totalFieldCost = fields.reduce((sum, exp) => {
    const hoursData = getFieldHoursForExpense(fieldHours, exp.id);
    const itemHoursUsed = hoursData.reduce((s, fh) => s + (parseFloat(fh.hours_played) || 0), 0);

    // Check if at least one participant has hours recorded for this field booking
    if (itemHoursUsed > 0) {
      totalHours += parseFloat(exp.total_quantity) || 0;
      return round(sum + parseFloat(exp.total_amount));
    } else if (parseFloat(exp.total_amount) > 0) {
      unallocatedExpenses.push(exp.description || exp.item_name);
    }
    return sum;
  }, 0);

  return {
    totalBoxCost,
    totalFieldCost,
    totalExpenses: round(totalBoxCost + totalFieldCost),
    adminFee: round((totalBoxCost + totalFieldCost) * (parseFloat(adminFeePercent) || 0) / 100),
    adminFeePercent: parseFloat(adminFeePercent) || 0,
    boxes,
    fields,
    totalUsedQuantity,
    totalTotalQuantity,
    totalHours,
    warnings: unallocatedExpenses
  };
};

export const calculateBalances = ({
  sessionParticipants = [],
  expenses = [],
  fieldHours = [],
  excludeBoxUsers = {},
  payments = [],
  adminFeePercent = 0,
}) => {
  if (!sessionParticipants || sessionParticipants.length === 0) return [];

  // O(n) lookup map for costs
  const participantCosts = new Map();
  sessionParticipants.forEach((p) => participantCosts.set(p.id, 0));

  const { totalBoxCost } = calculateTotals(expenses, fieldHours);

  // Distribute costs
  expenses.forEach((exp) => {
    if (exp.expense_type === "field") {
      const totalFieldCost = parseFloat(exp.total_amount) || 0;
      const hoursData = getFieldHoursForExpense(fieldHours, exp.id);
      const totalHoursUsed = hoursData.reduce(
        (sum, fh) => sum + (parseFloat(fh.hours_played) || 0),
        0
      );

      if (totalHoursUsed > 0) {
        hoursData.forEach((fh) => {
          if (participantCosts.has(fh.participant_id)) {
            const userHours = parseFloat(fh.hours_played) || 0;
            const proportion = userHours / totalHoursUsed;
            const share = round(totalFieldCost * proportion);
            participantCosts.set(
              fh.participant_id,
              round(participantCosts.get(fh.participant_id) + share)
            );
          }
        });
      }
    }
  });

  // Distribute box costs equally among non-excluded participants
  const peoplePayingBoxes = sessionParticipants.filter(
    (p) => !excludeBoxUsers[p.id]
  );

  if (peoplePayingBoxes.length > 0 && totalBoxCost > 0) {
    const costPerPerson = round(totalBoxCost / peoplePayingBoxes.length);
    peoplePayingBoxes.forEach((p) => {
      if (participantCosts.has(p.id)) {
        participantCosts.set(
          p.id,
          round(participantCosts.get(p.id) + costPerPerson)
        );
      }
    });
  }

  // Distribute admin fee equally among ALL session participants
  const feePercent = parseFloat(adminFeePercent) || 0;
  if (feePercent > 0 && sessionParticipants.length > 0) {
    const { totalBoxCost: bc, totalFieldCost: fc } = calculateTotals(expenses, fieldHours);
    const adminFeeAmount = round((bc + fc) * feePercent / 100);
    const feePerPerson = round(adminFeeAmount / sessionParticipants.length);
    sessionParticipants.forEach((p) => {
      if (participantCosts.has(p.id)) {
        participantCosts.set(
          p.id,
          round(participantCosts.get(p.id) + feePerPerson)
        );
      }
    });
  }

  // O(n) lookup for payments
  const paymentMap = new Map();
  payments.forEach(pay => paymentMap.set(pay.participant_id, pay));

  return sessionParticipants
    .map((p) => {
      const payment = paymentMap.get(p.id);
      return {
        id: p.id,
        name: p.name,
        profile_image_url: p.profile_image_url,
        linked_user_id: p.linked_user_id || null,
        owes: participantCosts.get(p.id) || 0,
        hasPaid: payment?.has_paid || false,
        paidAt: payment?.paid_at,
      };
    })
    .sort((a, b) => {
      if (a.hasPaid !== b.hasPaid) {
        return a.hasPaid ? 1 : -1;
      }
      return b.owes - a.owes;
    });
};

export const filterOverdueUsers = (allUnpaidUsers = []) => {
  const grouped = groupOverdueDebts(allUnpaidUsers);
  return Object.values(grouped).map(group => group.latestPayment);
};

export const groupOverdueDebts = (allUnpaidUsers = []) => {
  // Normalize "one week ago" to start of day
  const oneWeekAgo = new Date();
  oneWeekAgo.setHours(0, 0, 0, 0);
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

  const grouped = {};

  allUnpaidUsers.forEach((payment) => {
    if (!payment || !payment.sessions) return;

    const sessionDate = new Date(payment.sessions.session_date);
    sessionDate.setHours(0, 0, 0, 0);

    if (isNaN(sessionDate.getTime())) return;

    const participantId = payment.participant_id;
    const amount = parseFloat(payment.amount_owed) || 0;

    // Only count as bad debt if:
    // 1. Session is > 7 days old
    // 2. User hasn't paid
    // 3. Amount is significantly > 0 (handle rounding errors)
    if (sessionDate < oneWeekAgo && !payment.has_paid && amount > 0.01) {
      if (!grouped[participantId]) {
        grouped[participantId] = {
          participantId,
          name: payment.participants?.name || "unknown",
          profile_image_url: payment.participants?.profile_image_url,
          linked_user_id: payment.participants?.linked_user_id || null,
          sessions: [],
          totalAmount: 0,
          latestPayment: payment // Reference for Badge/Sidebar
        };
      }

      grouped[participantId].sessions.push({
        id: payment.session_id || payment.sessions?.id, // Correctly point to session ID
        paymentId: payment.id,
        sessionName: payment.sessions?.session_name,
        sessionDate: payment.sessions?.session_date,
        amount: amount,
      });

      // Ensure we add numbers, not concatenate strings
      grouped[participantId].totalAmount = round(grouped[participantId].totalAmount + amount);

      // Keep track of the most recent overdue payment for reference
      const currentLatestDate = new Date(grouped[participantId].latestPayment.sessions.session_date);
      if (sessionDate > currentLatestDate) {
        grouped[participantId].latestPayment = payment;
      }
    }
  });

  return grouped;
};
